import { Injectable, inject } from '@angular/core';
import { CatalogsFacade } from '../../catalog/facade/catalogs.facade';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import { CategoriesFacade } from '../../categories/facade/categories.facade';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  combineLatestWith,
} from 'rxjs';
import {
  BoelsBreadcrumb,
  BoelsCategoryHierarchy,
  BreadcrumbsModel,
  BreadcrumbsWrapper,
  PageTemplate,
} from '@boels/models';
import { UrlSafePipe } from '@boels/helpers';
import { CmsFacade } from '../../cms/facade/cms.facade';
import { ProductFacade } from '../../product';
import { Store } from '@ngrx/store';
import {
  selectGlobalProductSearchPageQueryTerm,
  selectGlobalProductSearchPageTotalResults,
} from '../../global-product-search/selectors/global-product-search.selector';
import { TranslocoService } from '@jsverse/transloco';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbsService {
  private cmsFacade = inject(CmsFacade);
  private catalogsFacade = inject(CatalogsFacade);
  private cultureFacade = inject(CultureFacade);
  private siteContextFacade = inject(SiteContextFacade);
  private categoryFacade = inject(CategoriesFacade);
  private productFacade = inject(ProductFacade);
  private urlSafePipe = inject(UrlSafePipe);
  private store = inject(Store);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);

  private homeItem: BreadcrumbsModel = {
    code: 'header.breadcrumb.home',
    name: 'Home',
  };

  categoryCrumbs$: Observable<BreadcrumbsWrapper | undefined> = combineLatest([
    this.categoryFacade.getActiveCategory(),
    this.cultureFacade.getActiveCulture(),
    this.siteContextFacade.getActiveBaseSiteEntity(),
  ]).pipe(
    switchMap(([activeCategory, activeCulture, activeBaseSite]) => {
      if (activeBaseSite?.uid && activeCulture && activeCategory)
        return this.catalogsFacade
          .getCategoryForId(activeBaseSite.uid, activeCulture, activeCategory)
          .pipe(
            filter(Boolean),
            map((category: BoelsCategoryHierarchy) => {
              if (category.boelsBreadcrumbs) {
                const activeElement: BreadcrumbsModel = {
                  code: category.id,
                  name: category.name,
                };
                return {
                  breadcrumbs: [
                    this.homeItem,
                    ...category.boelsBreadcrumbs.map((boelsBreadcrumb) => {
                      return {
                        name: boelsBreadcrumb.categoryName,
                        code: boelsBreadcrumb.categoryCode,
                      };
                    }),
                    activeElement,
                  ],
                } as BreadcrumbsWrapper;
              }
              return undefined;
            }),
            map((breadcrumbsWrapper) => {
              if (breadcrumbsWrapper?.breadcrumbs) {
                breadcrumbsWrapper.breadcrumbs =
                  breadcrumbsWrapper?.breadcrumbs?.map(
                    (item: BreadcrumbsModel, index) => {
                      return {
                        ...item,
                        url: this.buildUrlFromData(
                          breadcrumbsWrapper?.breadcrumbs,
                          index + 1,
                        ),
                      };
                    },
                  );
              }
              return breadcrumbsWrapper;
            }),
          );
      return of(undefined);
    }),
  );

  productCumbs$: Observable<BreadcrumbsWrapper | undefined> = this.productFacade
    .getCurrentProductAsObservable()
    .pipe(
      map((product) => {
        if (product?.boelsBreadcrumbs) {
          const activeElement: BreadcrumbsModel = {
            code: product.code,
            name: product.name,
            url: product.url,
          };
          return {
            title: product.name,
            breadcrumbs: [
              this.homeItem,
              ...product.boelsBreadcrumbs.map(
                (boelsBreadcrumb: BoelsBreadcrumb) => {
                  return {
                    name: boelsBreadcrumb.categoryName,
                    code: boelsBreadcrumb.categoryCode,
                  };
                },
              ),
              activeElement,
            ],
          } as BreadcrumbsWrapper;
        }
        return undefined;
      }),
      map((breadcrumbsWrapper) => {
        if (breadcrumbsWrapper?.breadcrumbs) {
          breadcrumbsWrapper.breadcrumbs = breadcrumbsWrapper?.breadcrumbs?.map(
            (item: BreadcrumbsModel, index) => {
              return {
                ...item,
                url: item.url?.includes('/p/') //do not change url if its the product url
                  ? item.url
                  : this.buildUrlFromData(
                      breadcrumbsWrapper?.breadcrumbs,
                      index + 1,
                    ),
              };
            },
          );
        }
        return breadcrumbsWrapper;
      }),
    );

  checkoutLoginCrumbs$: Observable<BreadcrumbsWrapper | undefined> =
    this.cmsFacade.getActivePage().pipe(
      map((page) => {
        if (page?.title) {
          return {
            title: page.title,
            breadcrumbs: [],
          } as BreadcrumbsWrapper;
        }
        return undefined;
      }),
    );

  storeFinderCrumbs$: Observable<BreadcrumbsWrapper | undefined> =
    this.cmsFacade.getActivePage().pipe(
      combineLatestWith(this.router.events),
      filter(([, event]) => event instanceof NavigationEnd),
      map(([page]) => {
        const storeFinderDetailsRegex = /\/branches\/.+\/.+/;
        const isStoreFinderDetailsPage = storeFinderDetailsRegex.test(
          this.router.routerState.snapshot.url,
        );
        if (page?.title) {
          return {
            title: page.title,
            breadcrumbs: [],
            isStoreFinderDetailsPage,
          } as BreadcrumbsWrapper;
        }
        return undefined;
      }),
    );

  searchResultCrumbs$: Observable<BreadcrumbsWrapper | undefined> =
    combineLatest([
      this.store.select(selectGlobalProductSearchPageTotalResults),
      this.store.select(selectGlobalProductSearchPageQueryTerm),
    ]).pipe(
      switchMap(([totalCount, queryTerm]): Observable<BreadcrumbsWrapper> => {
        if (totalCount !== undefined && totalCount >= 0 && queryTerm) {
          return this.translocoService
            .selectTranslate(
              'searchBox.resultFor',
              { count: totalCount, query: queryTerm },
              'header',
            )
            .pipe(
              take(1),
              map((breadcrumbString) => ({
                title: breadcrumbString,
                breadcrumbs: [{ name: '' }, { name: breadcrumbString }],
              })),
            );
        } else {
          return of({
            title: '',
            breadcrumbs: [],
          });
        }
      }),
    );

  pageNotFoundCrumbs$: Observable<BreadcrumbsWrapper | undefined> =
    this.cmsFacade.getActivePage().pipe(
      switchMap((page) =>
        this.translocoService
          .selectTranslate('breadcrumb.pageNotFound', {}, 'header')
          .pipe(
            take(1),
            map((pageNotFoundString) => {
              if (page?.title) {
                return {
                  title: page.title,
                  breadcrumbs: [{ name: '' }, { name: pageNotFoundString }],
                } as BreadcrumbsWrapper;
              }
              return undefined;
            }),
          ),
      ),
    );

  private static appendCategoryCode(url: string, code?: string) {
    return url + 'c/' + code;
  }

  getBreadCrumbData(): Observable<BreadcrumbsWrapper | undefined> {
    return this.cmsFacade.getActivePage().pipe(
      switchMap((page) => {
        switch (page?.template) {
          case PageTemplate.CHECKOUTACCOUNT:
            return this.checkoutLoginCrumbs$;
          case PageTemplate.PRODUCTLIST:
            return this.categoryCrumbs$;
          case PageTemplate.CATEGORY:
            return this.categoryCrumbs$;
          case PageTemplate.PRODUCT:
            return this.productCumbs$;
          case PageTemplate.SEARCHRESULTSLIST:
            return this.searchResultCrumbs$;
          case PageTemplate.STOREFINDER:
            return this.storeFinderCrumbs$;
          case PageTemplate.ERRORPAGE:
            return this.pageNotFoundCrumbs$;
          default:
            return of(undefined);
        }
      }),
    );
  }

  private buildUrlFromData(
    breadcrumbList: BreadcrumbsModel[],
    index: number,
  ): string {
    let url = '/';
    if (index > 1) {
      breadcrumbList.slice(1, index).forEach((breadcrumb: BreadcrumbsModel) => {
        url = url + this.urlSafePipe.transform(breadcrumb.name) + '/';
      });
      url = BreadcrumbsService.appendCategoryCode(
        url,
        breadcrumbList[index - 1].code,
      );
    }
    return url;
  }
}
